'use client';

import { useState, useCallback } from 'react';
import { X, Send, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';

/* ── Types ─────────────────────────────────────────────────────────────── */

interface DiscussionFormProps {
  onCreated?: () => void;
  onClose?: () => void;
}

/* ── Component ─────────────────────────────────────────────────────────── */

export default function DiscussionForm({ onCreated, onClose }: DiscussionFormProps) {
  const { user, profile, session, ensureSession } = useAuth();

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('general');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const displayName = profile?.full_name || user?.email?.split('@')[0] || 'Anonymous';
  const businessName = ''; // Could be pulled from profile/business data in the future

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError(null);

      if (!user) {
        setError('You must be signed in to post.');
        return;
      }

      if (!title.trim()) {
        setError('Title is required.');
        return;
      }
      if (!content.trim()) {
        setError('Please describe your feedback.');
        return;
      }

      setSubmitting(true);
      try {
        let accessToken = session?.access_token || null;
        if (!accessToken) {
          const recovered = await ensureSession();
          accessToken = recovered?.access_token || null;
        }
        if (!accessToken) {
          setError('Could not verify your session. Please sign in again.');
          return;
        }

        const res = await fetch('/api/feedback', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            title: title.trim(),
            content: content.trim(),
            category,
            author_name: displayName,
            author_business: businessName || null,
          }),
        });

        const data = await res.json();
        if (!res.ok || !data.ok) {
          setError(data.error || 'Failed to create discussion.');
          return;
        }

        setTitle('');
        setContent('');
        setCategory('general');
        onCreated?.();
        onClose?.();
      } catch {
        setError('Something went wrong. Please try again.');
      } finally {
        setSubmitting(false);
      }
    },
    [user, session, ensureSession, title, content, category, displayName, businessName, onCreated, onClose],
  );

  const categories = [
    { value: 'general', label: 'General' },
    { value: 'feature', label: 'Feature Request' },
    { value: 'bug', label: 'Bug Report' },
    { value: 'question', label: 'Question' },
  ];

  return (
    <div
      className="rounded-[var(--pub-radius-xl)] p-6"
      style={{
        background: 'var(--pub-surface-card)',
        border: '1px solid var(--pub-border-subtle)',
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3
          className="text-base font-bold"
          style={{ color: 'var(--pub-text-primary)' }}
        >
          Start a discussion
        </h3>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full transition-colors hover:bg-[var(--pub-surface-base)]"
            style={{ color: 'var(--pub-text-muted)' }}
            aria-label="Close form"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Auth gate */}
      {!user && (
        <p
          className="mt-3 text-sm"
          style={{ color: 'var(--pub-text-secondary)' }}
        >
          You need to{' '}
          <a
            href="/auth/signin"
            style={{ color: 'var(--pub-ember)', fontWeight: 600 }}
          >
            sign in
          </a>{' '}
          to post a discussion.
        </p>
      )}

      {/* Form */}
      {user && (
        <form onSubmit={handleSubmit} className="mt-4 space-y-4">
          {/* Category */}
          <div>
            <label
              className="mb-1.5 block text-xs font-medium uppercase tracking-wide"
              style={{ color: 'var(--pub-text-muted)' }}
            >
              Category
            </label>
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <button
                  key={cat.value}
                  type="button"
                  onClick={() => setCategory(cat.value)}
                  className="inline-flex h-8 items-center rounded-full px-3 text-xs font-medium transition-colors"
                  style={{
                    background:
                      category === cat.value ? 'var(--pub-ember)' : 'var(--pub-surface-base)',
                    color:
                      category === cat.value
                        ? 'var(--color-legacy-white)'
                        : 'var(--pub-text-secondary)',
                    border: `1px solid ${
                      category === cat.value
                        ? 'var(--pub-ember)'
                        : 'var(--pub-border-subtle)'
                    }`,
                  }}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>

          {/* Title */}
          <div>
            <label
              htmlFor="discussion-title"
              className="mb-1.5 block text-xs font-medium uppercase tracking-wide"
              style={{ color: 'var(--pub-text-muted)' }}
            >
              Title
            </label>
            <input
              id="discussion-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Brief summary of your feedback"
              maxLength={200}
              className="h-10 w-full rounded-[var(--pub-radius-md)] border bg-[var(--pub-surface-base)] px-3 text-sm outline-none transition-colors placeholder:text-[var(--pub-text-muted)] focus:border-[var(--pub-ember)]"
              style={{
                borderColor: 'var(--pub-border-default)',
                color: 'var(--pub-text-primary)',
              }}
            />
          </div>

          {/* Content */}
          <div>
            <label
              htmlFor="discussion-content"
              className="mb-1.5 block text-xs font-medium uppercase tracking-wide"
              style={{ color: 'var(--pub-text-muted)' }}
            >
              Description
            </label>
            <textarea
              id="discussion-content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Describe your feedback, feature request, or question in detail..."
              rows={4}
              maxLength={5000}
              className="w-full rounded-[var(--pub-radius-md)] border bg-[var(--pub-surface-base)] p-3 text-sm outline-none transition-colors placeholder:text-[var(--pub-text-muted)] focus:border-[var(--pub-ember)]"
              style={{
                borderColor: 'var(--pub-border-default)',
                color: 'var(--pub-text-primary)',
                resize: 'vertical',
                minHeight: '100px',
              }}
            />
            <p
              className="mt-1 text-right text-xs"
              style={{ color: 'var(--pub-text-muted)' }}
            >
              {content.length}/5000
            </p>
          </div>

          {/* Posting as */}
          <p className="text-xs" style={{ color: 'var(--pub-text-muted)' }}>
            Posting as <span style={{ color: 'var(--pub-text-secondary)' }}>{displayName}</span>
          </p>

          {/* Error */}
          {error && (
            <p className="text-sm font-medium" style={{ color: 'var(--color-legacy-graphic-red)' }}>
              {error}
            </p>
          )}

          {/* Submit */}
          <Button
            type="submit"
            disabled={submitting || !title.trim() || !content.trim()}
            className="h-10 w-full"
            style={{ borderRadius: 'var(--pub-radius-pill)' }}
          >
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Posting...
              </>
            ) : (
              <>
                <Send className="mr-2 h-4 w-4" />
                Post discussion
              </>
            )}
          </Button>
        </form>
      )}
    </div>
  );
}
